/* eslint-disable max-len */
import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { ClearedCheckLayout } from '@app/models/system-settings';
import { NgForTrackByIdDirective } from '@app/shared/directives/ng-for-track-by-id.directive';
import { NumbersOnlyDirective } from '@app/shared/directives/numbers-only.directive';
import { DuplicateValueValidatorDirective } from '@app/shared/directives/validators/duplicate-value-validator.directive';
import { FieldsOverlappingValidatorDirective } from './directives/cleared-check-layout-fields-validator.directive';
import { ClearedCheckLayoutService } from '../services/cleared-check-layout.service';

@Component({
	selector: 'ss-system-setup-cleared-check-layout',
	templateUrl: './cleared-check-layout.component.html',
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		FieldsOverlappingValidatorDirective,
		DuplicateValueValidatorDirective,
		MatRadioModule,
		MatFormFieldModule,
		NgFor,
		NgForTrackByIdDirective,
		NumbersOnlyDirective,
	],
})
export class ClearedCheckLayoutComponent implements OnChanges {
	@Input() bankId: number;
	@Input() isReadOnly = false;
	@Input() isNcUnit = false;
	@ViewChild('clearedCheckLayoutForm') clearedCheckLayoutForm: NgForm;
	//This is used for the NG1 page dirty form detection
	@Output() form: EventEmitter<NgForm> = new EventEmitter<NgForm>();

	fileType: string;
	clearedCheckLayout = {} as ClearedCheckLayout;
	private originalClearedCheckLayout: string;

	constructor(private readonly service: ClearedCheckLayoutService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.bankId && changes.bankId.currentValue && changes.bankId.currentValue > 0) {
			this.loadData();
		}
	}

	save(): void {
		if (this.isReadOnly) {
			// User should not reach this, but stop them if they do.
			return;
		}

		if (this.fileType === 'NCBANK') {
			this.clearedCheckLayout.IsNcBank = true;
			this.clearedCheckLayout.DelimitedFile = false;
		} else if (this.fileType === 'DELIMITED') {
			this.clearedCheckLayout.IsNcBank = false;
			this.clearedCheckLayout.DelimitedFile = true;
		} else if (this.fileType === 'FIXED') {
			this.clearedCheckLayout.IsNcBank = false;
			this.clearedCheckLayout.DelimitedFile = false;
		}
		this.service.updateForBank(this.bankId, this.clearedCheckLayout).subscribe((data: ClearedCheckLayout) => {
			this.clearedCheckLayout = data;
			this.originalClearedCheckLayout = JSON.stringify(this.clearedCheckLayout);
			this.clearedCheckLayoutForm.form.markAsPristine();
			this.clearedCheckLayoutForm.form.markAsUntouched();
		});
	}

	cancelSave(): void {
		this.clearedCheckLayout = JSON.parse(this.originalClearedCheckLayout) as ClearedCheckLayout;
		this.clearedCheckLayoutForm.form.markAsPristine();
		this.clearedCheckLayoutForm.form.markAsUntouched();
	}

	private loadData(): void {
		this.service.getByBankId(this.bankId).subscribe((data: ClearedCheckLayout) => {
			if (data != null) {
				this.clearedCheckLayout = data;
				if (this.clearedCheckLayout.IsNcBank) {
					this.fileType = 'NCBANK';
				} else if (this.clearedCheckLayout.DelimitedFile) {
					this.fileType = 'DELIMITED';
				} else {
					this.fileType = 'FIXED';
				}
			} else {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				this.clearedCheckLayout = {
					Id: 0,
					BankId: this.bankId,
					DelimitedFile: false,
					IsNcBank: false,
					ClearedCheckLayoutFields: [],
				} as ClearedCheckLayout;
				this.fileType = null;
			}

			if (!this.clearedCheckLayout.ClearedCheckLayoutFields) {
				this.clearedCheckLayout.ClearedCheckLayoutFields = [];
			}

			if (this.clearedCheckLayout.ClearedCheckLayoutFields.filter((item) => item.FieldName === 'Account #').length === 0) {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				this.clearedCheckLayout.ClearedCheckLayoutFields.push({ Id: 0, FieldName: 'Account #', FieldColumn: 1 });
			}
			if (this.clearedCheckLayout.ClearedCheckLayoutFields.filter((item) => item.FieldName === 'Check #').length === 0) {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				this.clearedCheckLayout.ClearedCheckLayoutFields.push({ Id: 0, FieldName: 'Check #', FieldColumn: 2 });
			}
			if (this.clearedCheckLayout.ClearedCheckLayoutFields.filter((item) => item.FieldName === 'Amount').length === 0) {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				this.clearedCheckLayout.ClearedCheckLayoutFields.push({ Id: 0, FieldName: 'Amount', FieldColumn: 3 });
			}
			if (this.clearedCheckLayout.ClearedCheckLayoutFields.filter((item) => item.FieldName === 'Date').length === 0) {
				// eslint-disable-next-line @typescript-eslint/naming-convention
				this.clearedCheckLayout.ClearedCheckLayoutFields.push({ Id: 0, FieldName: 'Date', FieldColumn: 4 });
			}
			this.originalClearedCheckLayout = JSON.stringify(this.clearedCheckLayout);

			//TODO: (remove when page is converted to NG11) This is used for the NG1 page dirty form detection
			this.form.emit(this.clearedCheckLayoutForm);
		});
	}
}
